<template>
  <div class="detection-event-container">
    <div
      class="mt-5 default-message mx-5 text-center text-slot detection-event-waiting"
      v-if="waiting"
    >
      <p class="mb-0 mx-auto text-center font-medium-5">
        <strong class="text-capitalize">
          {{ $tc("camera.title", 1)}}
        </strong>
      </p>
      <validation-observer v-slot="{ handleSubmit }">
        <b-form
          class="auth-login-form mt-2"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <v-select
            v-model="idCameraProxy"
            :options="formattedOptions"
            :filterable="false"
            @search="onSearch"
            :clearable="false"
            :reduce="(val) => val.value"
            input-id="camera_proxy"
            @option:selected="optionSelect"
            @option:deselected="optionDeselect"
            multiple
          >
            <template #list-footer>
              <li class="pagination">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  :disabled="!hasPrevPage"
                  @click="prevPage"
                >
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  :disabled="!hasNextPage"
                  @click="nextPage"
                >
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </b-button>
              </li>
            </template>
          </v-select>

          <b-button
            variant="primary"
            class="mr-1 mt-1 mb-1 text-capitalize btn-sm"
            type="submit"
          >
            {{ $t("actions.add") }}
          </b-button>
        </b-form>
      </validation-observer>
    </div>
    <b-container v-show="!waiting" fluid class="detection-event-body">
      <b-row class="detection-event-body__row">
        <b-col cols="6" md="6" class="p-0 detection-event-body__col">
          <vue-perfect-scrollbar
            ref="infoScrollbar"
            :settings="perfectScrollbarSettings"
            class="detection-info scroll-area"
          >
            <div v-if="detectionEvent">
              <lpr-detection
                v-if="detectionEvent.event_type == 'lpr'"
                :detectionEvent="detectionEvent"
              />

              <fr-detection
                v-if="detectionEvent.event_type == 'fr'"
                :detectionEvent="detectionEvent"
              />

              <generic-detection
                v-if="
                  detectionEvent.event_type != 'lpr' && detectionEvent.event_type != 'fr'
                "
                :detectionEvent="detectionEvent"
              />
            </div>
          </vue-perfect-scrollbar>
        </b-col>
        <b-col cols="6" md="6" class="p-0 detection-event-body__col">
          <vue-perfect-scrollbar
            ref="tableScrollbar"
            :settings="perfectScrollbarSettings"
            class="detection-table scroll-area"
          >
            <div class="m-1 detection-toolbar">
              <div class="detection-toolbar__entries">
                <label class="text-capitalize mb-0">{{ $t("table.show") }}</label>
                <v-select
                  v-model="perPage"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
                />
                <label class="text-capitalize mb-0">{{ $t("table.entries") }}</label>
              </div>

              <div class="detection-toolbar__actions">
                <feather-icon
                  :title="play ? $t('detection_event.pause') : $t('detection_event.play')"
                  :icon="play ? 'PauseCircleIcon' : 'PlayCircleIcon'"
                  class="text-capitalize cursor-pointer"
                  v-b-tooltip.hover
                  @click="setPlay()"
                  size="25"
                />
                <svg v-if="!play" xmlns="http://www.w3.org/2000/svg"
                  width="25" height="25"
                  fill="currentColor"
                  class="text-capitalize cursor-pointer bi bi-arrow-clockwise"
                  viewBox="0 0 16 16"
                  :title="$t('detection_event.update')"
                  v-b-tooltip.hover
                  @click="refreshTable()"
                >
                  <path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
                  <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
                </svg>
                <b-button
                  pill
                  v-b-modal.metadata_search_modal
                  variant="info"
                  class="btn-sm text-capitalize"
                  type="button"
                  v-b-tooltip.hover
                  :title="$t('filters')"
                >
                  <feather-icon icon="FilterIcon" size="15" />
                </b-button>
              </div>
            </div>
            <b-table
              ref="refListTable"
              class="table table-sm table-bordered"
              :fields="fields"
              :items="fetchList"
              :current-page="currentPage"
              :per-page="perPage"
              @row-selected="onRowSelected"
              select-mode="single"
              selectable
              selected-variant="info"
              :tbody-tr-class="rowClass"
            >
            </b-table>
            <div class="d-flex justify-content-center">
              <span class="text-muted text-capitalize">{{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}</span>
            </div>
            <div class="d-flex justify-content-center mb-2">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalList"
                :per-page="perPage"
                prev-class="prev-item"
                next-class="next-item"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
              ></b-pagination>
            </div>
          </vue-perfect-scrollbar>
        </b-col>
      </b-row>

      <metadata-search-modal @search="onSearchFilters()" @clear="clearSearchFilters()">
        <template v-slot:main-filters>
          <filters
            :unit-filter.sync="unitFilter"
            :unit-options="unitOptions"
            :tenant-filter.sync="tenantFilter"
            :tenant-options="tenantOptions"
            :camera-filter.sync="cameraFilter"
            :camera-options="cameraOptions"
            :event-type-filter.sync="eventTypeFilter"
            :event-type-options="eventTypeOptions"
            :provider-filter.sync="providerFilter"
            :provider-options="providerOptions"
            :start-datetime.sync="startDatetimeFilter"
            :end-datetime.sync="endDatetimeFilter"
            :alarm-filter.sync="alarmFilter"
          />
        </template>
        <template v-slot:meta-filters>
          <meta-filters-component v-model="objectTypeData" />
        </template>
      </metadata-search-modal>
    </b-container>
  </div>
</template>

<script>
import store from "@/store";
import vSelect from "vue-select";
import Filters from "@/views/detection_event/Filters.vue";
import axios from "@axios";
import { BButton, BTable, BCard } from "bootstrap-vue";
import EventEmitter from "@/libs/DOMEventEmitter";
import MetadataSearchModal from "@/components/MetadataSearchModal.vue";
import MetaFiltersComponent from "@/components/MetaFiltersComponent";
import { formatFilters as buildMetadataFilters } from "@/components/MetaFiltersComponent/helpers";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import FrDetection from "@/views/components/DetectionEventSlot/FrDetection.vue";
import LprDetection from "@/views/components/DetectionEventSlot/LprDetection.vue";
import GenericDetection from "@/views/components/DetectionEventSlot/GenericDetection.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import formValidation from "@core/comp-functions/forms/form-validation";
import { debounce } from "lodash";
import moment from "moment-timezone";

const eventEmitter = new EventEmitter();

export default {
  components: {
    BButton,
    BTable,
    BCard,
    vSelect,
    Filters,
    MetadataSearchModal,
    MetaFiltersComponent,
    LprDetection,
    FrDetection,
    GenericDetection,
    VuePerfectScrollbar,
    ValidationProvider,
    ValidationObserver,
    formValidation,
  },
  props: {
    tabInfo: Object,
    data: Object,
  },
  data() {
    const savedFilters = this.getSavedFilters();

    return {
      waiting: savedFilters.cameraFilter.length === 0,
      fields: [
        { label: "target", key: "", sortable: true },
        { label: this.$t("detection_event.event_type"), key: "formatted_event_type", sortable: true },
        { label: this.$tc("camera.title", 1), key: "camera_proxy.camera.name", sortable: true },
        { label: this.$t("date.created_at"), key: "created_at", sortable: true, formatter: (value, key, item) => {
            return  moment(item.created_at).format("DD/MM/YYYY HH:mm:ss")
          }
        },
      ],
      detectionEvent: null,
      totalRows: null,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      unitFilter: savedFilters.unitFilter,
      unitOptions: [],
      tenantFilter: savedFilters.tenantFilter,
      tenantOptions: [],
      cameraFilter: savedFilters.cameraFilter,
      cameraOptions: [],
      eventTypeFilter: savedFilters.eventTypeFilter,
      eventTypeOptions: [],
      providerFilter: savedFilters.providerFilter,
      providerOptions: [],
      startDatetimeFilter: savedFilters.startDatetimeFilter,
      endDatetimeFilter: savedFilters.endDatetimeFilter,
      alarmFilter: savedFilters.alarmFilter,
      totalList: 0,
      objectTypeData: savedFilters.objectTypeData,
      filters: {},
      perfectScrollbarSettings: { maxScrollbarLength: 60 },
      currentPageCam: 1,
      search: null,
      meta: null,
      limit: 8,
      options: [],
      idCameraProxy: [],
      tempCameras: [],
      play: true,
      selectedRow: null,
      temp: false,
      refresh: false,
      slotResizeObserver: null,
      onSlotResize: null,
    };
  },
  setup() {
    const toast = useToast();
    return {
      toast,
    };
  },
  watch: {
    currentPageCam() {
      this.fetchData()
    },
  },
  computed: {
    dataMeta: function () {
      let localItemsCount = this.$refs.refListTable
        ? this.$refs.refListTable.localItems.length
        : 0;
      return {
        from: this.perPage * (this.currentPage - 1) + (localItemsCount ? 1 : 0),
        to: this.perPage * (this.currentPage - 1) + localItemsCount,
        of: this.totalList,
      };
    },
    tabSlots() {
      return this.tabInfo && store.state.grid.tabs[this.tabInfo.id]
        ? store.state.grid.tabs[this.tabInfo.id].slots
        : [];
    },
    hasNextPage() {
      if (this.meta) {
        return this.currentPage + 1 <= this.meta;
      }
      return false;
    },
    formattedOptions() {
      return this.options.map((camera_proxy) => ({
        label: camera_proxy.camera.name == null ? null : camera_proxy.camera.name,
        value: camera_proxy.id,
      }));
    },
    hasPrevPage() {
      return  this.currentPageCam - 1 > 0;
    },
  },
  created() {
    this.persistFilters = debounce(this.persistFilters, 400);
  },
  mounted() {
    this.observeSlotResize();
    this.filterList();

    /*eventEmitter.on(`create:detection_event`, (data) => {
      this.syncWithCamera(data.detail);
    });*/

    if (this.data && this.data.in_app_source === SOURCES.ALARM_VIEW) {
      this.setAlarmViewConfig();
    } else if (!this.waiting) {
      this.cameraFilter.forEach((cameraProxyId) => {
        this.setDetectionEventWS(cameraProxyId);
      });
    }

    this.fetchData();
  },
  beforeDestroy() {
    this.unobserveSlotResize();

    if (this.persistFilters.cancel) {
      this.persistFilters.cancel();
    }
  },
  methods: {
    setAlarmViewConfig() {
      this.tabSlots.forEach((slot) => {
        const cameraProxyId =
          slot.type === SLOT_TYPES.CAM_LIVE && slot.data && slot.data.camera
            ? slot.data.camera.id_camera_proxy
            : null;

        if (cameraProxyId) {
          this.syncWithCamera({ id_camera_proxy: cameraProxyId }, false);
        }
      });
    },
    syncWithCamera(data, persist = true) {
      if (this.cameraFilter.length > 0) {
        if (!this.cameraFilter.includes(data.id_camera_proxy)) {
          this.cameraFilter.unshift(data.id_camera_proxy);
        }
      } else {
        this.cameraFilter.push(data.id_camera_proxy);
      }

      this.setDetectionEventWS(data.id_camera_proxy);
      this.refetchData();

      if (persist) {
        this.persistFilters();
      }

      this.waiting = false;
      this.toast({
        component: ToastificationContent,
        props: {
          title: this.$t("grid.slots.info.detection_events.default.title"),
          icon: "CheckIcon",
          variant: "success",
        },
      });
    },
    setDetectionEventWS(cameraProxyId) {
      if (!window.Echo) return;

      window.Echo.private(`detection-event-${cameraProxyId}`).listen(
        ".created",
        (data) => {
          if(this.play){
            this.refetchData();
            this.temp = true;
          }
        }
      );
    },
    onRowSelected(item) {
      if(this.refresh){
        this.refresh = false;
        return
      }

      if(!this.temp){
        if(item.length > 0 ) {
          this.detectionEvent = item[0];
          this.selectedRow = item[0].created_at;
        }
        else{
          this.selectedRow = null;
        }
      }

      this.temp= false;
    },
    fetchList(ctx, callback) {
      store
        .dispatch("detection_event/getAll", {
          max: this.perPage,
          "page[n]": this.currentPage,
          sortBy: "id",
          sortDesc: true,
          ...this.buildFilters(),
        })
        .then((response) => {
          callback(response.data);
          this.setFields(response.data[0]);
          if(!this.selectedRow){
            this.detectionEvent = response.data[0];
          }
          this.totalList = response.meta.total;
        });
    },
    filterList() {
      axios("v1/tenants").then((response) => {
        this.tenantOptions = response.data.data.map((tenant) => ({
          label: tenant.name,
          value: tenant.id,
        }));
      });

      axios("v1/unit?hasDetectionEvent=true").then((response) => {
        this.unitOptions = response.data.data.map((unit) => ({
          label: unit.description,
          value: unit.id,
        }));
      });

      axios("v1/proxy_camera?hasDetectionEvent=true&max=5000").then((response) => {
        this.cameraOptions = response.data.data.map((camera) => ({
          label: camera.camera.name,
          value: camera.id,
        }));
      });

      axios("v1/services/detection-events/get-catalog").then((response) => {
        this.providerOptions = response.data.data.providers.map((provider) => ({
          label: provider.label,
          value: provider.value,
        }));

        response.data.data.providers.forEach((provider) => {
          provider.detections.forEach((event_type) => {
            if (
              !this.eventTypeOptions
                .map((value) => value.value)
                .includes(event_type.value)
            ) {
              this.eventTypeOptions.push({
                label: event_type.label,
                value: event_type.value,
              });
            }
          });
        });
      });
    },
    buildFilters() {
      this.filters = {
        alarm: this.alarmFilter,
        unit: this.unitFilter,
        tenant: this.tenantFilter,
        cameras: this.cameraFilter,
        provider: this.providerFilter,
        event_type: this.eventTypeFilter,
        from: this.startDatetimeFilter,
        to: this.endDatetimeFilter,
        ...buildMetadataFilters(this.objectTypeData),
      };
      return this.filters;
    },
    clearSearchFilters() {
      this.alarmFilter = null;
      this.unitFilter = null;
      this.tenantFilter = null;
      this.cameraFilter = [];
      this.providerFilter = null;
      this.eventTypeFilter = null;
      this.startDatetimeFilter = null;
      this.endDatetimeFilter = null;
      this.objectTypeData = null;
      this.persistFilters();
    },
    getSavedFilters() {
      const saved =
        this.data && this.data.filters
          ? JSON.parse(JSON.stringify(this.data.filters))
          : {};

      return {
        unitFilter: saved.unitFilter ?? null,
        tenantFilter: saved.tenantFilter ?? null,
        cameraFilter: Array.isArray(saved.cameraFilter) ? saved.cameraFilter : [],
        eventTypeFilter: saved.eventTypeFilter ?? null,
        providerFilter: saved.providerFilter ?? null,
        startDatetimeFilter: saved.startDatetimeFilter || null,
        endDatetimeFilter: saved.endDatetimeFilter || null,
        alarmFilter: saved.alarmFilter ?? null,
        objectTypeData: saved.objectTypeData ?? {},
      };
    },
    persistFilters() {
      this.$emit(
        "update:filters",
        JSON.parse(
          JSON.stringify({
            unitFilter: this.unitFilter,
            tenantFilter: this.tenantFilter,
            cameraFilter: this.cameraFilter,
            eventTypeFilter: this.eventTypeFilter,
            providerFilter: this.providerFilter,
            startDatetimeFilter: this.startDatetimeFilter,
            endDatetimeFilter: this.endDatetimeFilter,
            alarmFilter: this.alarmFilter,
            objectTypeData: this.objectTypeData,
          })
        )
      );
    },
    onSearchFilters() {
      this.persistFilters();
      this.refetchData();
    },
    refetchData() {
      if(this.$refs.refListTable){
        this.$refs.refListTable.refresh();
      }
    },
    setFields(data) {
      if (data != undefined) {
        switch (data.event_type) {
          case "lpr":
            this.fields[0].key = "data.plate";
            break;

          case "fr":
            this.fields[0].key = "luna_match.name";
            break;

          default:
            this.fields[0].key = "null";
            break;
        }
      }
    },
    observeSlotResize() {
      if (typeof ResizeObserver === "undefined") return;

      this.onSlotResize = debounce(() => this.updateScrollbars(), 100);
      this.slotResizeObserver = new ResizeObserver(this.onSlotResize);
      this.slotResizeObserver.observe(this.$el);
    },
    unobserveSlotResize() {
      if (this.onSlotResize) {
        this.onSlotResize.cancel();
        this.onSlotResize = null;
      }

      if (this.slotResizeObserver) {
        this.slotResizeObserver.disconnect();
        this.slotResizeObserver = null;
      }
    },
    updateScrollbars() {
      if (this.$refs.infoScrollbar) this.$refs.infoScrollbar.update();
      if (this.$refs.tableScrollbar) this.$refs.tableScrollbar.update();
    },
    fetchData() {
      return new Promise((resolve, reject) => {
        let params = {
          q: this.search,
          max: this.limit,
          "page[n]": this.currentPageCam,
          sortBy: "id",
          sortDesc: true,
        }
        axios('v1/proxy_camera?deviceType=camera', { params } )
          .then((response) => {
            this.meta = response.data.meta.last_page;
            this.options = response.data.data;
            resolve();
          })
          .catch(() => {
            console.log("SelectPaginateAjax: Error obteniendo la información");
            reject();
          });
      });
    },
    onSearch: debounce(async function  (query, loading) {
      if (query.length) {
        loading(true);
        this.search = query;
        await this.fetchData();
        loading(false);
      }
      else {
        this.currentPageCam = 1;
        this.search = "";
        this.fetchData();
      }
    }, 500),
    onSubmit() {
      if(this.idCameraProxy == 0){
        return this.toast({
          component: ToastificationContent,
          props: {
            title: this.$t("camera.cameras_validation"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },

        });
      }

      this.idCameraProxy.forEach(cameraProxyId => {
        this.syncWithCamera({'id_camera_proxy': cameraProxyId.value });
      })
    },
    nextPage() {
      this.currentPageCam += 1;
    },
    prevPage() {
      this.currentPageCam --
    },
    optionSelect(selected) {
      this.idCameraProxy = selected
      this.tempCameras = this.idCameraProxy
    },
    optionDeselect(deselected) {
      this.idCameraProxy = this.tempCameras.filter(camera_proxy => (camera_proxy.value != deselected.value))
      this.tempCameras = this.idCameraProxy
    },
    setPlay(){
      if(this.play){
        this.play = false
      }
      else{
        this.play = true
      }
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.created_at === this.selectedRow) return 'table-info'
    },
    refreshTable(){
      this.refresh = true;
      this.refetchData();
    }
  },
};
</script>

<style lang="scss" scoped>
.detection-event-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.detection-event-waiting {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.detection-event-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-width: 100%;
  padding: 0;

  &__row {
    flex: 1 1 auto;
    min-height: 0;
    margin: 0;
  }

  &__col {
    height: 100%;
    min-height: 0;
  }
}

.detection-info,
.detection-table {
  height: 100%;
}

.detection-toolbar {
  display: flex;
  flex-wrap: wrap-reverse;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;

  &__entries,
  &__actions {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  &__entries {
    flex-wrap: wrap;
    row-gap: 0.25rem;

    label {
      white-space: nowrap;
    }
  }

  &__actions {
    flex-wrap: nowrap;
    margin-left: auto;
    gap: 0.25rem;
  }

  .per-page-selector {
    flex: 0 0 auto;
    width: 90px;
  }
}
.waiting-detection {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.pagination {
  display: flex;
  justify-content: space-around;
  margin: 0.25rem 0.25rem 0;
}
.pagination button {
  flex-grow: 1;
  &:first-child {
    margin-right: 5px;
  }
}
.pagination button:hover {
  cursor: pointer;
}
</style>
